<template>
  <div class="lead-card" @click="navigateToLead">
    <div class="lead-card__avatar">
      <AvatarElement
        :title="lead.name ? lead.name.substring(0, 2).toUpperCase() : 'LD'"
        :color="'var(--brand-500)'"
        :textColor="'white'"
      />
    </div>
    <div class="lead-card__info">
      <div class="lead-card__name-row">
        <h4 class="lead-card__name">{{ displayName || $t('crm.no_name_entered') }}</h4>
        <span v-if="isCompany" class="lead-card__type-badge">
          <BaseIcon name="building" size="11" />
          {{ $t('lead_details.customer_type.company') }}
        </span>
      </div>
      <div class="lead-card__email">{{ lead.email }}</div>
      <div v-if="lead.phone" class="lead-card__phone">{{ lead.phone }}</div>
      <span class="lead-card__status" :class="`lead-card__status--${lead.status || 'new'}`">
        <div class="status-dot" :class="`status-dot--${lead.status || 'new'}`"></div>
        {{ getStatusLabel(lead.status) }}
      </span>
    </div>
  </div>
</template>

<script>
  import { computed, getCurrentInstance } from 'vue';
  import { useRouter } from 'vue-router';
  import AvatarElement from '@/components/partials/Avatar.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import { useUIStore } from '@/store/uiStore';

  export default {
    name: 'LeadCard',
    components: {
      AvatarElement,
      BaseIcon,
    },
    props: {
      lead: {
        type: Object,
        required: true,
      },
    },

    setup(props) {
      const instance = getCurrentInstance();
      const router = useRouter();
      const $t = instance.appContext.config.globalProperties.$t;
      const uiStore = useUIStore();

      const isCompany = computed(() => props.lead.customer_type === 'company');
      const displayName = computed(() =>
        isCompany.value && props.lead.company_name ? props.lead.company_name : props.lead.name
      );

      const getStatusLabel = (status) => {
        const statusLabels = {
          new: $t('crm.statuses.new'),
          contacted: $t('crm.statuses.contacted'),
          introduction: $t('crm.statuses.introduction'),
          qualified: $t('crm.statuses.qualified'),
          converted: $t('crm.statuses.converted'),
          lost: $t('crm.statuses.lost'),
        };
        return statusLabels[status] || $t('crm.statuses.new');
      };

      const navigateToLead = () => {
        uiStore.closeAllModals();
        router.push({ name: 'LeadDetail', params: { id: props.lead.id } });
      };

      return {
        isCompany,
        displayName,
        getStatusLabel,
        navigateToLead,
      };
    },
  };
</script>

<style scoped lang="scss">
  .lead-card {
    @include card;
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: var(--blue-300);
    }

    &__avatar {
      flex-shrink: 0;
    }

    &__info {
      flex: 1;
      min-width: 0;
    }

    &__name-row {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
      margin: 0 0 0.25rem 0;
    }

    &__name {
      font-size: 1.125rem;
      font-weight: 600;
      color: var(--slate-900);
      margin: 0;
    }

    &__type-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      font-size: 0.6875rem;
      font-weight: 600;
      color: var(--indigo-700);
      background: var(--indigo-50);
      border: 1px solid var(--indigo-100);
      padding: 0.125rem 0.5rem;
      border-radius: 0.625rem;
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }

    &__email {
      font-size: 0.875rem;
      color: var(--slate-600);
      margin: 0 0 0.25rem 0;
    }

    &__phone {
      font-size: 0.875rem;
      color: var(--slate-600);
      margin: 0 0 0.5rem 0;
    }

    &__status {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      padding: 4px 8px;
      border-radius: 0.5rem;
      font-size: 0.875rem;
      font-weight: 500;
      text-transform: lowercase;

      &--new {
        background-color: var(--blue-50);
        color: var(--blue-700);
        border: 1px solid var(--blue-200);
      }

      &--contacted {
        background-color: var(--amber-50);
        color: var(--amber-700);
        border: 1px solid var(--amber-200);
      }

      &--introduction {
        background-color: var(--indigo-50);
        color: var(--indigo-700);
        border: 1px solid var(--indigo-200);
      }

      &--qualified {
        background-color: var(--teal-50);
        color: var(--teal-700);
        border: 1px solid var(--teal-200);
      }

      &--converted {
        background-color: var(--green-50);
        color: var(--green-700);
        border: 1px solid var(--green-200);
      }

      &--lost {
        background-color: var(--red-50);
        color: var(--red-700);
        border: 1px solid var(--red-200);
      }
    }
  }

  .status-dot {
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    flex-shrink: 0;

    &--new {
      background-color: var(--blue-500);
    }

    &--contacted {
      background-color: var(--amber-500);
    }

    &--introduction {
      background-color: var(--indigo-500);
    }

    &--qualified {
      background-color: var(--teal-500);
    }

    &--converted {
      background-color: var(--green-500);
    }

    &--lost {
      background-color: var(--red-500);
    }
  }
</style>
